import { Capacitor } from "@capacitor/core";
import { ModalOpenButton, useScreenWidth } from "@msys/ui";
import { ExitToApp as LogoutIcon } from "@mui/icons-material";
import { Button, ButtonProps, IconButton } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { ROUTER_BASENAME } from "../../environment.js";
import { ConfirmModal } from "../commons/modals/ConfirmModal.js";
import { assertNever } from "../utils.js";
import { buildSignedOutRedirectUri } from "./signup/signupAuthOptions.js";
import { useAuth } from "./useAuth.js";

export const LogoutButton = ({
  color = "warning",
  type: _type,
  sx,
  fullWidth = true,
}: {
  color?: ButtonProps["color"];
  type?: "icon" | "button";
  sx?: ButtonProps["sx"];
  fullWidth?: boolean;
}) => {
  const { auth } = useAuth();
  const { t } = useTranslate("SidebarItems");
  const { isMaxPhone } = useScreenWidth();
  const type = _type ?? (isMaxPhone ? "icon" : "button");

  return (
    <ModalOpenButton
      Modal={ConfirmModal}
      modalProps={{
        title: t("Are you sure you want to log out?"),
        handleConfirm: async () => {
          // On the web a signed-out visitor starts again on the welcome page,
          // which offers Google and the work email for both login and signup.
          // The native app keeps its own logout callback.
          await auth.logout(
            Capacitor.isNativePlatform()
              ? undefined
              : {
                  redirectUri: buildSignedOutRedirectUri({
                    origin: window.location.origin,
                    basename: ROUTER_BASENAME,
                  }),
                }
          );
        },
      }}
    >
      {type === "icon" ? (
        <IconButton color={color} aria-label={t("Log out")} sx={sx}>
          <LogoutIcon />
        </IconButton>
      ) : type === "button" ? (
        <Button
          fullWidth={fullWidth}
          sx={sx}
          startIcon={<LogoutIcon />}
          variant="outlined"
          color={color}
        >
          {t("Log out")}
        </Button>
      ) : (
        assertNever(type)
      )}
    </ModalOpenButton>
  );
};
